import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaEnvelope, FaSpinner } from "react-icons/fa";
import MailLayout from "../components/correo/MailLayout";
import AccountsSidebar from "../components/correo/AccountsSidebar";
import InboxPanel from "../components/correo/InboxPanel";
import MessageViewer from "../components/correo/MessageViewer";
import ComposeModal from "../components/correo/ComposeModal";
import LoadingScreen from "../components/ui/temp-screens/LoadingScreen";
import * as mailService from "../services/mailService";
import { useMailSocket } from "../hooks/useMailSocket";

const Correo = () => {
  const user = useSelector((state) => state.auth.user);
  const authToken = useSelector((state) => state.auth.token);
  const isAdmin = user?.area?.toUpperCase() === "ADMINISTRACION";

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  const [connectedAccounts, setConnectedAccounts] = useState({});
  const [connectingAccounts, setConnectingAccounts] = useState({});

  const [activeAccountId, setActiveAccountId] = useState(null);
  const [activeFolder, setActiveFolder] = useState("INBOX");
  const [loadingUnified, setLoadingUnified] = useState(false);
  const [unifiedConnectProgress, setUnifiedConnectProgress] = useState({ connected: 0, total: 0 });

  const [folders, setFolders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedMessageContext, setSelectedMessageContext] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(false);

  const [composeOpen, setComposeOpen] = useState(false);
  const [replyData, setReplyData] = useState(null);
  const [firmaTrabajador, setFirmaTrabajador] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [unifiedHasMore, setUnifiedHasMore] = useState(false);
  const [unifiedOldestDate, setUnifiedOldestDate] = useState(null);
  const [loadingMoreUnified, setLoadingMoreUnified] = useState(false);
  const searchDebounceRef = useRef(null);
  const unifiedLoadIdRef = useRef(0);
  const hasAutoSelectedUnified = useRef(false);
  const activeAccount = accounts.find((a) => a.id === activeAccountId);
  const isSearchMode = searchQuery.trim().length > 0;

  const handleNewMessage = useCallback(
    (data) => {
      const isUnified = activeAccountId === "all";
      const isSingleAccount = data.mailAccountId === activeAccountId && activeFolder === "INBOX";
      if (isUnified || isSingleAccount) {
        setMessages((prev) => {
          const exists = prev.some(
            (m) => (m.mailAccountId ? m.mailAccountId === data.mailAccountId && m.uid === data.uid : m.uid === data.uid)
          );
          if (exists) return prev;
          const account = accounts.find((a) => a.id === data.mailAccountId);
          const enriched = {
            ...data,
            mailAccountName: account?.nombre || account?.imapUser || data.mailAccountId,
          };
          return [enriched, ...prev];
        });
        setPagination((prev) => ({ ...prev, total: prev.total + 1 }));
      }
      toast.info(`Nuevo correo de ${data.from}: ${data.subject}`, {
        autoClose: 4000,
      });
    },
    [activeAccountId, activeFolder, accounts]
  );

  const connectedAccountIds = Object.keys(connectedAccounts).filter(
    (id) => connectedAccounts[id]
  );

  useMailSocket(authToken, connectedAccountIds, handleNewMessage);

  // Cargar firma del trabajador una sola vez al tener el user
  useEffect(() => {
    if (!user?.id) return;
    const loadFirmaTrabajador = async () => {
      try {
        const res = await mailService.getTrabajador(user.id);
        setFirmaTrabajador(res.data?.firmaCorreo || null);
      } catch (e) {
        console.log("Error obteniendo firma de trabajador:", e?.response?.status, e?.message);
      }
    };
    loadFirmaTrabajador();
  }, [user?.id]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const [accountsRes, mailAccountsRes] = await Promise.all([
        mailService.getAccounts(),
        mailService.getMailAccounts().catch(() => ({ data: { docs: [] } })),
      ]);
      const baseAccounts = accountsRes.data.accounts || [];
      const mailDocs = mailAccountsRes.data?.docs || [];
      const colorMap = Object.fromEntries(mailDocs.map((m) => [m.id, m.color]));
      const textColorMap = Object.fromEntries(mailDocs.map((m) => [m.id, m.textColor]));
      setAccounts(
        baseAccounts.map((a) => ({
          ...a,
          color: colorMap[a.id] || null,
          textColor: textColorMap[a.id] || null,
        }))
      );
    } catch (e) {
      toast.error("Error al obtener las cuentas de correo");
      console.log(e);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleConnect = async (accountId) => {
    if (connectedAccounts[accountId]) {
      try {
        await mailService.disconnectAccount(accountId);
        setConnectedAccounts((prev) => {
          const next = { ...prev };
          delete next[accountId];
          return next;
        });
        if (activeAccountId === accountId) {
          setMessages([]);
          setFolders([]);
          setSelectedMessage(null);
        }
        toast.success("Cuenta desconectada");
      } catch (e) {
        toast.error("Error al desconectar la cuenta");
        console.log(e);
      }
      return;
    }

    setConnectingAccounts((prev) => ({ ...prev, [accountId]: true }));
    try {
      await mailService.connectAccount(accountId);
      setConnectedAccounts((prev) => ({ ...prev, [accountId]: true }));
      toast.success("Cuenta conectada correctamente");

      if (activeAccountId === accountId || !activeAccountId) {
        setActiveAccountId(accountId);
        setActiveFolder("INBOX");
        await loadFolders(accountId);
        await loadMessages(accountId, "INBOX", 1);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Error al conectar la cuenta";
      toast.error(detail);
      console.log(e);
    } finally {
      setConnectingAccounts((prev) => {
        const next = { ...prev };
        delete next[accountId];
        return next;
      });
    }
  };

  const handleSelectUnified = async () => {
    setActiveAccountId("all");
    setActiveFolder("INBOX");
    setSelectedMessage(null);
    setSelectedMessageContext(null);
    setSearchQuery("");
    setFilters({});
    setUnifiedHasMore(false);
    setUnifiedOldestDate(null);
    setMessages([]);
    setFolders([]);
    setPagination({ page: 1, limit: 100, total: 0 });
    setLoadingMessages(true);
    setLoadingUnified(true);
    const alreadyConnected = Object.keys(connectedAccounts).filter((id) => connectedAccounts[id]).length;
    setUnifiedConnectProgress({ connected: alreadyConnected, total: accounts.length });

    try {
      const toConnect = accounts.filter((a) => !connectedAccounts[a.id]);
      for (const acc of toConnect) {
        try {
          await mailService.connectAccount(acc.id);
          setConnectedAccounts((prev) => ({ ...prev, [acc.id]: true }));
          setUnifiedConnectProgress((prev) => ({ ...prev, connected: prev.connected + 1 }));
        } catch (e) {
          const detail = e.response?.data?.detail || "Error al conectar";
          toast.error(`${acc.nombre || acc.imapUser}: ${detail}`);
        }
      }
      if (toConnect.length > 0) toast.success("Cuentas conectadas");
      await loadUnifiedInbox({});
    } catch (e) {
      toast.error("Error al cargar el inbox unificado");
      console.log(e);
    } finally {
      setLoadingUnified(false);
      setLoadingMessages(false);
    }
  };

  // Auto-seleccionar "Todas" y conectar cuentas al cargar la página (solo una vez)
  useEffect(() => {
    if (!loadingAccounts && accounts.length > 0 && !hasAutoSelectedUnified.current) {
      hasAutoSelectedUnified.current = true;
      handleSelectUnified();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Solo al cargar cuentas, handleSelectUnified no en deps
  }, [loadingAccounts, accounts]);

  const loadUnifiedInbox = useCallback(async (filterParams, append = false) => {
    const f = filterParams ?? filters;
    if (!append) {
      unifiedLoadIdRef.current += 1;
    }
    const currentLoadId = unifiedLoadIdRef.current;
    if (append) {
      setLoadingMoreUnified(true);
    } else {
      setLoadingMessages(true);
    }
    try {
      const params = {
        folder: "INBOX",
        limit: 100,
      };
      if (f?.since) params.since = f.since;
      if (f?.from) params.from = f.from;
      if (f?.mailAccountIds?.length) params.mailAccountIds = f.mailAccountIds;
      if (append && unifiedOldestDate) {
        params.before = unifiedOldestDate;
      } else if (f?.before && !append) {
        params.before = f.before;
      }
      const res = await mailService.getUnifiedInbox(params);
      const data = res.data;
      const newMessages = data.messages || [];
      if (currentLoadId !== unifiedLoadIdRef.current) return;
      if (append) {
        setMessages((prev) => [...prev, ...newMessages]);
      } else {
        setMessages(newMessages);
      }
      setUnifiedHasMore(!!data.hasMore);
      setUnifiedOldestDate(data.oldestDate || null);
      setPagination((prev) => ({
        ...prev,
        page: 1,
        total: append ? prev.total : (data.total ?? prev.total),
      }));
    } catch (e) {
      if (currentLoadId !== unifiedLoadIdRef.current) return;
      toast.error("Error al cargar el inbox unificado");
      console.log(e);
      if (!append) setMessages([]);
    } finally {
      if (currentLoadId !== unifiedLoadIdRef.current) return;
      if (append) {
        setLoadingMoreUnified(false);
      } else {
        setLoadingMessages(false);
      }
    }
  }, [filters, unifiedOldestDate]);

  const loadMoreUnified = useCallback(async () => {
    if (!unifiedHasMore || !unifiedOldestDate || loadingMoreUnified) return;
    await loadUnifiedInbox(filters, true);
  }, [unifiedHasMore, unifiedOldestDate, loadingMoreUnified, loadUnifiedInbox, filters]);

  const handleFiltersApply = useCallback((nextFilters) => {
    setFilters(nextFilters);
    setMessages([]);
    setUnifiedHasMore(false);
    setUnifiedOldestDate(null);
    setPagination((prev) => ({ ...prev, total: 0 }));
    loadUnifiedInbox(nextFilters);
  }, [loadUnifiedInbox]);

  const handleFiltersClear = useCallback(() => {
    setFilters({});
    setMessages([]);
    setUnifiedHasMore(false);
    setUnifiedOldestDate(null);
    setPagination((prev) => ({ ...prev, total: 0 }));
    loadUnifiedInbox({});
  }, [loadUnifiedInbox]);

  const handleSelectAccount = async (accountId) => {
    setActiveAccountId(accountId);
    setActiveFolder("INBOX");
    setSelectedMessage(null);
    setSelectedMessageContext(null);
    setSearchQuery("");
    setMessages([]);
    setPagination({ page: 1, limit: 20, total: 0 });
    setLoadingMessages(true);

    if (!connectedAccounts[accountId]) {
      await handleConnect(accountId);
    } else {
      await loadFolders(accountId);
      await loadMessages(accountId, "INBOX", 1);
    }
  };

  const loadFolders = async (accountId) => {
    try {
      const res = await mailService.getFolders(accountId);
      const all = res.data.folders || [];
      // Ocultar carpetas namespace virtuales como [Gmail] que no son seleccionables
      const filtered = all.filter(
        (f) => !/^\[[^\]]+\]$/.test(f.path)
      );
      setFolders(filtered);
    } catch (e) {
      console.log(e);
    }
  };

  const loadMessages = useCallback(async (accountId, folder, page) => {
    setLoadingMessages(true);
    try {
      const res = folder === "INBOX"
        ? await mailService.getInbox(accountId, page, pagination.limit)
        : await mailService.getFolderMessages(accountId, folder, page, pagination.limit);
      const msgs = (res.data.messages || []).map((m) => ({ ...m, folder: m.folder || folder }));
      setMessages(msgs);
      setPagination((prev) => ({
        ...prev,
        page: res.data.page || page,
        total: res.data.total || 0,
      }));
    } catch (e) {
      toast.error("Error al cargar los mensajes");
      console.log(e);
    } finally {
      setLoadingMessages(false);
    }
  }, [pagination.limit]);

  const loadSearch = useCallback(async (accountId, folder, q) => {
    setLoadingMessages(true);
    try {
      const res = await mailService.searchMail(accountId, q.trim(), folder, 50);
      setMessages(res.data.messages || []);
      setPagination((prev) => ({
        ...prev,
        page: 1,
        total: res.data.total || 0,
      }));
    } catch (e) {
      toast.error("Error al buscar correos");
      console.log(e);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!value.trim()) {
      if (activeAccountId === "all") {
        loadUnifiedInbox(filters);
      } else if (activeAccountId) {
        loadMessages(activeAccountId, activeFolder, 1);
      }
      return;
    }
    if (activeAccountId === "all") return;
    const trimmed = value.trim();
    if (trimmed.length < 2) return;
    searchDebounceRef.current = setTimeout(() => {
      if (activeAccountId && activeAccountId !== "all" && value.trim().length >= 2) {
        loadSearch(activeAccountId, activeFolder, value.trim());
      }
      searchDebounceRef.current = null;
    }, 1200);
  }, [activeAccountId, activeFolder, loadMessages, loadSearch, loadUnifiedInbox, filters]);

  const displayedMessages =
    activeAccountId === "all" && searchQuery.trim()
      ? messages.filter(
          (m) =>
            m.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            m.from?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            m.to?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : messages;

  const handleSelectFolder = async (folderPath) => {
    setActiveFolder(folderPath);
    setSelectedMessage(null);
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (activeAccountId) {
      await loadMessages(activeAccountId, folderPath, 1);
    }
  };

  const handlePageChange = async (page) => {
    setPagination((prev) => ({ ...prev, page }));
    if (activeAccountId) {
      await loadMessages(activeAccountId, activeFolder, page);
    }
  };

  const handleSelectMessage = async (msg) => {
    const accountId = msg?.mailAccountId || activeAccountId;
    const folder = msg?.folder || activeFolder;
    const uid = msg?.uid;
    if (!accountId || uid == null) return;
    setSelectedMessageContext(activeAccountId === "all" ? { mailAccountId: accountId, uid, folder } : null);
    setLoadingMessage(true);
    try {
      const res = await mailService.getMessage(accountId, uid, folder);
      const messageData = res.data;
      setSelectedMessage(messageData);

      if (!messageData.seen) {
        try {
          await mailService.updateMessageSeen(accountId, uid, true, folder);
          setSelectedMessage((prev) => (prev ? { ...prev, seen: true } : prev));
          setMessages((prev) =>
            prev.map((m) => {
              const match = m.mailAccountId ? m.mailAccountId === accountId && m.uid === uid : m.uid === uid;
              return match ? { ...m, seen: true } : m;
            })
          );
          // toast.success(patchMsg);
        } catch (patchErr) {
          
          console.log("Error al marcar como leído:", patchErr);
        }
      }
    } catch (e) {
      toast.error("Error al leer el mensaje");
      console.log(e);
    } finally {
      setLoadingMessage(false);
    }
  };

  const handleMarkUnread = async () => {
    const accountId = selectedMessageContext?.mailAccountId || activeAccountId;
    const folder = selectedMessageContext?.folder || activeFolder;
    const uid = selectedMessage?.uid;
    if (!accountId || uid == null) return;
    try {
      const res = await mailService.updateMessageSeen(accountId, uid, false, folder);
      const msg = res.data?.message || "Mensaje marcado como no leído";
      setSelectedMessage((prev) => (prev ? { ...prev, seen: false } : prev));
      setMessages((prev) =>
        prev.map((m) => {
          const match = m.mailAccountId ? m.mailAccountId === accountId && m.uid === uid : m.uid === uid;
          return match ? { ...m, seen: false } : m;
        })
      );
      toast.success(msg);
    } catch (e) {
      toast.error("Error al marcar como no leído");
      console.log(e);
    }
  };

  const handleDeleteMessage = async (msg) => {
    const accountId = msg?.mailAccountId || activeAccountId;
    const folder = msg?.folder || activeFolder;
    const uid = msg?.uid;
    if (!accountId || uid == null) return;
    if (!window.confirm("¿Mover este mensaje a la papelera?")) return;
    try {
      const foldersRes = await mailService.getFolders(accountId);
      const folders = foldersRes.data?.folders || [];
      const trashFolder = folders.find((f) =>
        /trash|papelera|deleted|eliminad/i.test(f.name || f.path || "")
      )?.path ?? "Trash";
      await mailService.moveMessage(accountId, uid, folder, trashFolder);
      setMessages((prev) =>
        prev.filter((m) =>
          m.mailAccountId
            ? !(m.mailAccountId === accountId && m.uid === uid)
            : m.uid !== uid
        )
      );
      if (selectedMessage?.uid === uid) {
        setSelectedMessage(null);
        setSelectedMessageContext(null);
      }
      toast.success("Mensaje movido a la papelera");
    } catch (e) {
      toast.error("Error al mover el mensaje a la papelera");
      console.log(e);
    }
  };

  const handleMoveMessage = async (msg) => {
    const accountId = msg?.mailAccountId || activeAccountId;
    const folder = msg?.folder || activeFolder;
    const uid = msg?.uid;
    if (!accountId || uid == null) return;
    const isInSpam = /spam|junk/i.test(folder || "");
    const isInTrash = /trash|papelera|deleted|eliminad/i.test(folder || "");
    const willMoveToInbox = isInSpam || isInTrash;
    if (willMoveToInbox) {
      if (!window.confirm("¿Devolver este mensaje a la bandeja de entrada?")) return;
    } else {
      if (!window.confirm("¿Mover este mensaje a la carpeta de spam?")) return;
    }
    try {
      let destination;
      if (willMoveToInbox) {
        destination = "INBOX";
      } else {
        const foldersRes = await mailService.getFolders(accountId);
        const folders = foldersRes.data?.folders || [];
        destination = folders.find((f) => /spam|junk/i.test(f.name || f.path || ""))?.path ?? "Junk";
      }
      await mailService.moveMessage(accountId, uid, folder, destination);
      setMessages((prev) =>
        prev.filter((m) =>
          m.mailAccountId
            ? !(m.mailAccountId === accountId && m.uid === uid)
            : m.uid !== uid
        )
      );
      if (selectedMessage?.uid === uid) {
        setSelectedMessage(null);
        setSelectedMessageContext(null);
      }
      toast.success(willMoveToInbox ? "Mensaje devuelto a la bandeja de entrada" : "Mensaje movido a spam");
    } catch (e) {
      toast.error(willMoveToInbox ? "Error al devolver el mensaje" : "Error al mover el mensaje a spam");
      console.log(e);
    }
  };

  const handleReply = (msg) => {
    setReplyData({
      to: msg.from,
      subject: msg.subject?.startsWith("Re:") ? msg.subject : `Re: ${msg.subject || ""}`,
      html: `<br/><br/><hr/><p><strong>El ${new Date(msg.date).toLocaleString("es-PE")} ${msg.from} escribió:</strong></p>${msg.html || `<p>${msg.text || ""}</p>`}`,
      defaultAccountId: msg?.mailAccountId || null,
    });
    setComposeOpen(true);
  };

  const handleCompose = () => {
    setReplyData(null);
    setComposeOpen(true);
  };

  const handleSend = async ({ mailAccountId, to, cc, bcc, subject, text, html, attachments }) => {
    if (!mailAccountId) {
      toast.error("Selecciona una cuenta para enviar");
      return;
    }
    await mailService.sendMessage({
      mailAccountId,
      to,
      cc,
      bcc,
      subject,
      text,
      html,
      attachments: attachments || [],
    });
    toast.success("Correo enviado correctamente");
  };

  const handleRefresh = () => {
    if (activeAccountId === "all") {
      loadUnifiedInbox(filters);
    } else if (activeAccountId) {
      if (isSearchMode) {
        loadSearch(activeAccountId, activeFolder, searchQuery.trim());
      } else {
        loadMessages(activeAccountId, activeFolder, pagination.page);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  if (loadingAccounts) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <FaEnvelope className="text-orange-500" />
          <h1 className="text-lg font-bold text-gray-800">Correo</h1>
        </div>
        <LoadingScreen text="Cargando cuentas de correo..." />
      </div>
    );
  }

  return (
    <div className="">

      {/* NavBar */}
      <div className="flex flex-row px-4 py-2 bg-gray-100">
        <div className="flex flex-row w-full items-center gap-4 justify-between overflow-x-auto overflow-y-hidden px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex items-center gap-4 py-4">
            <FaEnvelope className="text-zinc-700 text-2xl" />
            <h2 className="text-2xl font-bold text-zinc-700">Correo electrónico</h2>
          </div>
        </div>
      </div>

      <div className="relative">
        <MailLayout
          sidebar={
            <AccountsSidebar
            accounts={accounts}
            activeAccountId={activeAccountId}
            connectedAccounts={connectedAccounts}
            connectingAccounts={connectingAccounts}
            folders={folders}
            activeFolder={activeFolder}
            loadingUnified={loadingUnified}
            onSelectAccount={handleSelectAccount}
            onSelectUnified={handleSelectUnified}
            onToggleConnect={handleConnect}
            onSelectFolder={handleSelectFolder}
            onCompose={handleCompose}
          />
        }
        inbox={
          activeAccountId ? (
            <InboxPanel
              accountName={activeAccountId === "all" ? "Todas las cuentas" : activeAccount?.imapUser}
              activeAccountId={activeAccountId}
              folderName={activeFolder}
              messages={displayedMessages}
              unifiedLoadedCount={activeAccountId === "all" ? messages.length : undefined}
              accounts={accounts}
              selectedUid={selectedMessage?.uid}
              selectedMessageContext={selectedMessageContext}
              loading={loadingMessages}
              pagination={pagination}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              isSearchMode={isSearchMode}
              isUnifiedMode={activeAccountId === "all"}
              filters={filters}
              onFiltersApply={handleFiltersApply}
              onFiltersClear={handleFiltersClear}
              connectedAccountIds={connectedAccountIds}
              unifiedHasMore={unifiedHasMore}
              loadingMoreUnified={loadingMoreUnified}
              onLoadMoreUnified={loadMoreUnified}
              onSelectMessage={handleSelectMessage}
              onDeleteMessage={handleDeleteMessage}
              onMoveMessage={handleMoveMessage}
              currentFolder={activeFolder}
              onPageChange={handlePageChange}
              onRefresh={handleRefresh}
              canDelete={isAdmin}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <p className="text-xs">Selecciona una cuenta</p>
            </div>
          )
        }
        viewer={
          <MessageViewer
            message={selectedMessage}
            loading={loadingMessage}
            onReply={handleReply}
            onMarkUnread={selectedMessage?.seen ? handleMarkUnread : null}
            canDelete={isAdmin}
            onDelete={(uid) => {
              const msg = {
                uid,
                mailAccountId: selectedMessageContext?.mailAccountId || activeAccountId,
                folder: selectedMessageContext?.folder || activeFolder,
              };
              handleDeleteMessage(msg);
            }}
            onMoveMessage={() => {
              const msg = {
                uid: selectedMessage?.uid,
                mailAccountId: selectedMessageContext?.mailAccountId || activeAccountId,
                folder: selectedMessageContext?.folder || activeFolder,
              };
              handleMoveMessage(msg);
            }}
            currentFolder={selectedMessageContext?.folder || activeFolder}
            mailAccountId={selectedMessageContext?.mailAccountId || activeAccountId}
            folder={selectedMessageContext?.folder || activeFolder}
          />
        }
        />
        {loadingUnified && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-[2px]">
            <div className="flex flex-col items-center gap-2 w-full max-w-xs px-6">
              <span className="text-xs font-bold text-gray-700 ">
                Conectando emails
              </span>
              <span className="text-xs text-gray-700">
                {unifiedConnectProgress.connected} de {unifiedConnectProgress.total} cuentas conectadas
              </span>
              <div className="w-full h-1 mb-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-500 rounded-full transition-all duration-300 ease-out"
                  style={{
                    width: unifiedConnectProgress.total > 0
                      ? `${(unifiedConnectProgress.connected / unifiedConnectProgress.total) * 100}%`
                      : "0%",
                  }}
                />
              </div>
              <FaSpinner className="animate-spin text-gray-500" />
            </div>
          </div>
        )}
      </div>

      <ComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSend={handleSend}
        replyData={replyData}
        accounts={accounts}
        connectedAccounts={connectedAccounts}
        firmaTrabajador={firmaTrabajador}
      />
    </div>
  );
};

export default Correo;
